<script setup lang="ts">
import { computed } from 'vue'
import { Tooltip } from '@/shared/ui/Tooltip'

/**
 * A green / red / grey dot with the state's name in its tooltip — for a column too narrow
 * for a word, where the dot is one of several per row. A state worth reading outright is
 * a `StatusTag`.
 *
 * `null` is the grey dot and means "not known", which is not the same as `false`. Always
 * pass `text`: without it the dot has no accessible name and colour is the only thing
 * carrying the meaning.
 */
const props = withDefaults(
  defineProps<{
    text?: string
    status?: boolean | null
  }>(),
  { status: null }
)

const dotClass = computed(() => {
  if (props.status === true) return 'status-dot status-dot--success'
  if (props.status === false) return 'status-dot status-dot--danger'
  return 'status-dot status-dot--neutral'
})

const showTooltip = computed(() => !!props.text)
</script>

<template>
  <Tooltip v-if="showTooltip" :content="text" placement="top" effect="light">
    <span :class="dotClass" aria-hidden="true" />
  </Tooltip>
  <span v-else :class="dotClass" aria-hidden="true" />
</template>

<style lang="scss" scoped>
.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  cursor: pointer;
}

.status-dot--success {
  background-color: var(--el-color-success);
}

.status-dot--danger {
  background-color: var(--el-color-danger);
}

.status-dot--neutral {
  background-color: var(--el-text-color-placeholder);
}
</style>
